import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ArticleIcon from '@mui/icons-material/Article';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import HelpIcon from '@mui/icons-material/Help';
import HistoryIcon from '@mui/icons-material/History';
import HomeIcon from '@mui/icons-material/Home';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { Box, SvgIconProps } from '@mui/material';
import React from 'react';
import { getModelIconConfig } from '../../utils/ModelIconMapping';
import { Palette } from 'Palette';

const withRounding = (
  IconComponent: React.ComponentType<SvgIconProps>,
): React.FC<SvgIconProps & { tone?: keyof typeof Palette }> => {
  return (props: SvgIconProps & { tone?: keyof typeof Palette }) => {
    const { sx, tone = 'default', ...rest } = props;
    return (
      <Box
        className={`react-IconHelper-Rounded ${props.className || ''}`.trim()}
        sx={{
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '999px',
          backgroundColor: Palette[tone].background,
          color: Palette[tone].text,
          ...sx,
        }}
      >
        <IconComponent {...rest} />
      </Box>
    );
  };
};

export const withoutRounding = (
  IconComponent: React.ComponentType<SvgIconProps>,
): React.FC<SvgIconProps & { tone?: keyof typeof Palette }> => {
  return ({ ...props }: SvgIconProps & { tone?: keyof typeof Palette }) => (
    <Box
      sx={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Optionally use tone background for icon context
        // backgroundColor: Palette[tone].background,
      }}
    >
      <IconComponent {...props} />
    </Box>
  );
};

export type IconType =
  | 'Home'
  | 'Funds'
  | 'Portfolio'
  | 'Chat'
  | 'Contact'
  | 'Close'
  | 'Admin'
  | 'News'
  | 'Settings'
  | 'Actions'
  | 'Entity'
  | 'History'
  | 'Help'
  | 'Advisor';

export const IconMap: Readonly<{
  [K in IconType]: {
    iconElement: React.ComponentType<SvgIconProps>;
    icon: React.ComponentType<SvgIconProps & { tone?: keyof typeof Palette }>;
    iconRounded: React.ComponentType<SvgIconProps>;
  };
}> = {
  Home: {
    iconElement: HomeIcon,
    icon: withoutRounding(HomeIcon),
    iconRounded: withRounding(HomeIcon),
  },
  Portfolio: {
    iconElement: ShowChartIcon,
    icon: withoutRounding(ShowChartIcon),
    iconRounded: withRounding(ShowChartIcon),
  },
  Funds: {
    iconElement: ApartmentIcon,
    icon: withoutRounding(ApartmentIcon),
    iconRounded: withRounding(ApartmentIcon),
  },
  Chat: {
    iconElement: ChatIcon,
    icon: withoutRounding(ChatIcon),
    iconRounded: withRounding(ChatIcon),
  },
  Contact: {
    iconElement: PersonIcon,
    icon: withoutRounding(PersonIcon),
    iconRounded: withRounding(PersonIcon),
  },
  Close: {
    iconElement: CloseIcon,
    icon: withoutRounding(CloseIcon),
    iconRounded: withRounding(CloseIcon),
  },
  Admin: {
    iconElement: ManageAccountsIcon,
    icon: withoutRounding(ManageAccountsIcon),
    iconRounded: withRounding(ManageAccountsIcon),
  },
  News: {
    iconElement: ArticleIcon,
    icon: withoutRounding(ArticleIcon),
    iconRounded: withRounding(ArticleIcon),
  },
  Settings: {
    iconElement: SettingsIcon,
    icon: withoutRounding(SettingsIcon),
    iconRounded: withRounding(SettingsIcon),
  },
  Actions: {
    iconElement: AssignmentIcon,
    icon: withoutRounding(AssignmentIcon),
    iconRounded: withRounding(AssignmentIcon),
  },
  Entity: {
    iconElement: BusinessIcon,
    icon: withoutRounding(BusinessIcon),
    iconRounded: withRounding(BusinessIcon),
  },
  History: {
    iconElement: HistoryIcon,
    icon: withoutRounding(HistoryIcon),
    iconRounded: withRounding(HistoryIcon),
  },
  Help: {
    iconElement: HelpIcon,
    icon: withoutRounding(HelpIcon),
    iconRounded: withRounding(HelpIcon),
  },
  Advisor: {
    iconElement: AdminPanelSettings,
    icon: withoutRounding(AdminPanelSettings),
    iconRounded: withRounding(AdminPanelSettings),
  },
};

// Helper function to get model icons with consistent styling
export const getModelIconWithStyling = (modelType: string, rounded: boolean = false) => {
  const config = getModelIconConfig(modelType);
  if (!config) {
    return rounded ? withRounding(AssignmentIcon) : withoutRounding(AssignmentIcon);
  }
  return rounded ? withRounding(config.icon) : withoutRounding(config.icon);
};
